import React, { useState } from 'react';

export default function ContactForm({ defaultService = '' }) {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    opgavetype: defaultService || 'Vandværksservice & ledningsarbejde',
    besked: '',
    website_url: '', // Honeypot field - must stay empty
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.navn.trim()) newErrors.navn = 'Venligst indtast dit navn';
    if (!formData.telefon.trim()) {
      newErrors.telefon = 'Venligst indtast dit telefonnummer';
    } else if (!/^[+0-9\s-]{6,16}$/.test(formData.telefon.trim())) {
      newErrors.telefon = 'Indtast et gyldigt telefonnummer (min. 8 cifre)';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Venligst indtast din e-mailadresse';
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      newErrors.email = 'Indtast en gyldig e-mailadresse';
    }
    if (!formData.besked.trim()) newErrors.besked = 'Venligst beskriv opgaven kort';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          navn: formData.navn.trim(),
          telefon: formData.telefon.trim(),
          email: formData.email.trim(),
          opgavetype: formData.opgavetype,
          besked: formData.besked.trim(),
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage hurtigst muligt.',
        });
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          opgavetype: defaultService || 'Vandværksservice & ledningsarbejde',
          besked: '',
          website_url: '',
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSubmitStatus({
          success: false,
          message:
            errorData.message ||
            'Der opstod en fejl ved afsendelse. Ring gerne direkte til os på 40 30 97 08.',
        });
      }
    } catch (err) {
      setSubmitStatus({
        success: false,
        message: 'Forbindelsesfejl. Tjek din internetforbindelse eller ring til os på 40 30 97 08.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 shadow-lg border border-slate-200">
      <div className="mb-6 sm:mb-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-2">
          <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
          Uforpligtende henvendelse
        </span>
        <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
          Fortæl os om din opgave
        </h3>
        <p className="text-slate-600 text-sm sm:text-base mt-2">
          Udfyld formularen, så kontakter vi dig hurtigst muligt for en uforpligtende snak eller et godt tilbud.
        </p>
      </div>

      {submitStatus.message && (
        <div
          className={`mb-6 p-4 rounded-xl text-sm font-medium flex items-start gap-3 ${
            submitStatus.success
              ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
              : 'bg-rose-50 text-rose-900 border border-rose-200'
          }`}
          role="alert"
        >
          <i
            className={`fa-solid ${
              submitStatus.success ? 'fa-circle-check text-emerald-600' : 'fa-triangle-exclamation text-rose-600'
            } text-lg mt-0.5`}
            aria-hidden="true"
          />
          <div>{submitStatus.message}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {/* Antispam honeypot: Absolutely hidden with sr-only and position */}
        <input
          type="text"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          className="sr-only absolute -left-[9999px] top-auto w-1 h-1 overflow-hidden"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Navn */}
          <div>
            <label htmlFor="navn" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-1.5">
              Dit navn / Virksomhedsnavn <span className="text-emerald-600">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                id="navn"
                name="navn"
                required
                value={formData.navn}
                onChange={handleChange}
                placeholder="F.eks. Jens Hansen"
                className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                  errors.navn
                    ? 'border-rose-400 bg-rose-50/30 focus:ring-rose-400'
                    : 'border-slate-300 bg-white focus:border-emerald-600 focus:ring-emerald-500/20'
                }`}
              />
            </div>
            {errors.navn && <p className="text-rose-600 text-xs mt-1 font-medium">{errors.navn}</p>}
          </div>

          {/* Telefon */}
          <div>
            <label htmlFor="telefon" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-1.5">
              Telefonnummer <span className="text-emerald-600">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                id="telefon"
                name="telefon"
                required
                value={formData.telefon}
                onChange={handleChange}
                placeholder="F.eks. 40 30 97 08"
                className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                  errors.telefon
                    ? 'border-rose-400 bg-rose-50/30 focus:ring-rose-400'
                    : 'border-slate-300 bg-white focus:border-emerald-600 focus:ring-emerald-500/20'
                }`}
              />
            </div>
            {errors.telefon && <p className="text-rose-600 text-xs mt-1 font-medium">{errors.telefon}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-1.5">
              E-mailadresse <span className="text-emerald-600">*</span>
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="F.eks. kontakt@firma.dk"
                className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                  errors.email
                    ? 'border-rose-400 bg-rose-50/30 focus:ring-rose-400'
                    : 'border-slate-300 bg-white focus:border-emerald-600 focus:ring-emerald-500/20'
                }`}
              />
            </div>
            {errors.email && <p className="text-rose-600 text-xs mt-1 font-medium">{errors.email}</p>}
          </div>

          {/* Opgavetype */}
          <div>
            <label htmlFor="opgavetype" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-1.5">
              Hvad drejer opgaven sig om?
            </label>
            <select
              id="opgavetype"
              name="opgavetype"
              value={formData.opgavetype}
              onChange={handleChange}
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all cursor-pointer"
            >
              <option value="Vandværksservice & ledningsarbejde">Vandværksservice & ledningsarbejde</option>
              <option value="Autoriseret kloakarbejde & tilslutning">Autoriseret kloakarbejde & tilslutning</option>
              <option value="Belægning & anlæg">Belægning & anlæg</option>
              <option value="Vinterservice & snerydning">Vinterservice & snerydning</option>
              <option value="Andet entreprenørarbejde">Andet entreprenørarbejde</option>
            </select>
          </div>
        </div>

        {/* Beskrivelse */}
        <div>
          <label htmlFor="besked" className="block text-xs sm:text-sm font-semibold text-slate-700 mb-1.5">
            Beskrivelse af opgaven <span className="text-emerald-600">*</span>
          </label>
          <textarea
            id="besked"
            name="besked"
            rows={4}
            required
            value={formData.besked}
            onChange={handleChange}
            placeholder="Fortæl kort om hvad der skal laves, adresse/lokation og hvornår du ønsker arbejdet udført..."
            className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all resize-y ${
              errors.besked
                ? 'border-rose-400 bg-rose-50/30 focus:ring-rose-400'
                : 'border-slate-300 bg-white focus:border-emerald-600 focus:ring-emerald-500/20'
            }`}
          />
          {errors.besked && <p className="text-rose-600 text-xs mt-1 font-medium">{errors.besked}</p>}
        </div>

        {/* Submit button */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white font-bold text-base shadow-md hover:shadow-lg transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin text-white" aria-hidden="true" />
                <span>Sender henvendelse...</span>
              </>
            ) : (
              <>
                <span>Send henvendelse</span>
                <i className="fa-solid fa-paper-plane text-sm" aria-hidden="true" />
              </>
            )}
          </button>
          <p className="text-xs text-slate-500 mt-2.5">
            Vi besvarer henvendelser hurtigt. Du er også altid velkommen til at ringe på{' '}
            <a href="tel:40309708" className="text-emerald-700 font-semibold hover:underline whitespace-nowrap">
              40 30 97 08
            </a>
            .
          </p>
        </div>
      </form>
    </div>
  );
}
